import { getArea, getLength } from 'ol/sphere';
import Polygon from 'ol/geom/Polygon';
import { M_TO_FT, M_TO_KM, M_TO_MI, SCALABLE_ROTATABLE_LAYER } from '../../../util/constants';
import { Geometry, LineString, MultiPolygon, Point } from 'ol/geom';
import { FenceEntityType, MeasurementType } from '../../../util/enums';
import { getCenter, getHeight, getWidth } from 'ol/extent';
import { StyleFunction } from 'ol/style/Style';
import RenderFeature from 'ol/render/Feature';
import { Feature } from 'ol';
import { geometryTypeOfEntity } from '../commons';
import { GridRowData } from '@material-ui/data-grid';
import VectorSource from 'ol/source/Vector';
import { Map as OlMap } from 'ol';
import { Draw, Modify, Snap, Translate } from 'ol/interaction';
import { EditState } from './Props';
import { Coordinate } from 'ol/coordinate';

export type Segment = [[number, number], [number, number]];
type DrawOptions = ConstructorParameters<typeof Draw>[0];
type ModifyOptions = ConstructorParameters<typeof Modify>[0];
type SnapOptions = ConstructorParameters<typeof Snap>[0];
type TranslateOptions = ConstructorParameters<typeof Translate>[0];

type InteractionConstructor =
  | { new (options: DrawOptions): Draw }
  | { new (options: ModifyOptions): Modify }
  | { new (options: SnapOptions): Snap }
  | { new (options: TranslateOptions): Translate };

export const calculateArea = (polygon: Polygon, measurementType: MeasurementType | undefined) => {
  const area = getArea(polygon);
  switch (measurementType) {
    case MeasurementType.FT2:
      return (area * M_TO_FT * M_TO_FT).toFixed(2) + ' ' + 'ft<sup>2</sup>';
    case MeasurementType.KM2:
      return (area * M_TO_KM * M_TO_KM).toFixed(2) + ' km<sup>2</sup>';
    case MeasurementType.MI2:
      return (area * M_TO_MI * M_TO_MI).toFixed(2) + ' mi<sup>2</sup>';
    default:
      return area.toFixed(2) + ' ' + 'm<sup>2</sup>';
  }
};

export const calculateMeasurementDistance = (
  line: LineString,
  measurementType: MeasurementType | undefined,
) => {
  const length = getLength(line);
  switch (measurementType) {
    case MeasurementType.FT:
      return (length * M_TO_FT).toFixed(2) + ' ' + measurementType.toLowerCase();
    case MeasurementType.KM:
      return (length * M_TO_KM).toFixed(2) + ' ' + measurementType.toLowerCase();
    case MeasurementType.MI:
      return (length * M_TO_MI).toFixed(2) + ' ' + measurementType.toLowerCase();
    default:
      return length.toFixed(2) + ' ' + MeasurementType.M.toLowerCase();
  }
};

export const calculateCenterOfFence = (
  geometry: LineString | Polygon | MultiPolygon,
  type: FenceEntityType | undefined,
) => {
  let center: number[];
  const coordinates =
    type === FenceEntityType.Polygon
      ? geometry.getCoordinates()[0].slice(1)
      : type === FenceEntityType.Line
      ? (geometry as LineString).getCoordinateAt(0.5)
      : type === FenceEntityType.Multipolygon
      ? (geometry.getCoordinates() as number[][][])[0][0]
      : [];

  let minRadius = 1;
  if (type === FenceEntityType.Polygon) {
    let x = 0;
    let y = 0;
    let i = 0;

    (coordinates as number[][]).forEach(coordinate => {
      if (Array.isArray(coordinate) && coordinate.length > 1) {
        x += coordinate[0];
        y += coordinate[1];
        i++;
      }
    });
    center = [x / i, y / i];
  } else if (type === FenceEntityType.Multipolygon) {
    center = getCenter(geometry.getExtent());
  } else if (type === FenceEntityType.Line) {
    center = (geometry as LineString).getCoordinateAt(0.5);
  } else {
    center = getCenter(geometry.getExtent());
  }
  let sqDistances: number[] = [];
  if (Array.isArray(coordinates)) {
    sqDistances = (
      type === FenceEntityType.Polygon || type === FenceEntityType.Multipolygon
        ? (coordinates as number[][])
        : (coordinates as number[])
    ).map(coordinate => {
      if (Array.isArray(coordinate) && coordinate.length > 1) {
        const dx = coordinate[0] - center[0];
        const dy = coordinate[1] - center[1];
        return dx * dx + dy * dy;
      }
      return 0;
    });
    if (Array.isArray(sqDistances)) {
      minRadius = Math.sqrt(Math.max(...sqDistances)) / 3;
    }
  } else {
    minRadius = Math.max(getWidth(geometry.getExtent()), getHeight(geometry.getExtent())) / 3;
  }
  return {
    center: center,
    coordinates: coordinates,
    minRadius: minRadius,
    sqDistances: sqDistances,
  };
};

export const defaultScaleRotFenceStyle = (): StyleFunction => {
  return (feature: Feature<Geometry> | RenderFeature) => {
    feature.get('features').forEach((modifyFeature: Feature<Geometry>) => {
      const modifyGeometry = modifyFeature.get('modifyGeometry');
      if (modifyGeometry && feature.getGeometry() !== undefined) {
        const geometry = feature.getGeometry();
        if (!geometry) return;
        const point = getCenter(geometry.getExtent());
        let modifyPoint = modifyGeometry.point;
        if (!modifyPoint) {
          // save the initial geometry and vertex position
          modifyPoint = point;
          modifyGeometry.point = modifyPoint;
          modifyGeometry.geometry0 = modifyGeometry.geometry;
          // get anchor and minimum radius of vertices to be used
          const result = calculateCenterOfFence(
            modifyGeometry.geometry0,
            geometryTypeOfEntity(modifyFeature.getProperties() as GridRowData),
          );
          modifyGeometry.center = result.center;
          modifyGeometry.minRadius = result.minRadius;
        }

        const center = modifyGeometry.center;
        const minRadius = modifyGeometry.minRadius;
        let dx, dy;
        dx = modifyPoint[0] - center[0];
        dy = modifyPoint[1] - center[1];
        const initialRadius = Math.sqrt(dx * dx + dy * dy);
        if (initialRadius > minRadius && Array.isArray(point)) {
          const initialAngle = Math.atan2(dy, dx);
          dx = point[0] - center[0];
          dy = point[1] - center[1];
          const currentRadius = Math.sqrt(dx * dx + dy * dy);
          if (currentRadius > 0) {
            const currentAngle = Math.atan2(dy, dx);
            const geometry = modifyGeometry.geometry0.clone();
            geometry.scale(currentRadius / initialRadius, undefined, center);
            geometry.rotate(currentAngle - initialAngle, center);
            modifyGeometry.geometry = geometry;
          }
        }
      }
    });
  };
};

export const segmentsIntersect = ([p1, p2]: Segment, [q1, q2]: Segment): boolean => {
  const orientation = (a: number[], b: number[], c: number[]) =>
    Math.sign((b[1] - a[1]) * (c[0] - b[0]) - (b[0] - a[0]) * (c[1] - b[1]));

  const onSegment = (a: number[], b: number[], c: number[]) =>
    Math.min(a[0], c[0]) <= b[0] &&
    b[0] <= Math.max(a[0], c[0]) &&
    Math.min(a[1], c[1]) <= b[1] &&
    b[1] <= Math.max(a[1], c[1]);

  const o1 = orientation(p1, p2, q1);
  const o2 = orientation(p1, p2, q2);
  const o3 = orientation(q1, q2, p1);
  const o4 = orientation(q1, q2, p2);

  // Proper intersection
  if (o1 !== o2 && o3 !== o4) return true;

  // Colinear + overlapping cases
  if (o1 === 0 && onSegment(p1, q1, p2)) return true;
  if (o2 === 0 && onSegment(p1, q2, p2)) return true;
  if (o3 === 0 && onSegment(q1, p1, q2)) return true;
  if (o4 === 0 && onSegment(q1, p2, q2)) return true;

  return false;
};

export const edgeOverlapWithOtherPolygon = (outer: Geometry, inner?: Geometry): boolean => {
  // Must both be polygons
  if (!(outer instanceof Polygon)) return false;
  if (!(inner instanceof Polygon)) return false;
  if (outer === inner) return false;

  const outerCoords = outer.getCoordinates();
  const innerCoords = inner.getCoordinates();

  const outerSegments: Segment[] = [];

  // Build outer segments
  for (const ring of outerCoords) {
    for (let i = 0; i < ring.length - 1; i++) {
      outerSegments.push([
        [ring[i][0], ring[i][1]],
        [ring[i + 1][0], ring[i + 1][1]],
      ]);
    }
  }

  // Check inner edges against all outer edges
  for (const ring of innerCoords) {
    for (let i = 0; i < ring.length - 1; i++) {
      const innerSeg: Segment = [
        [ring[i][0], ring[i][1]],
        [ring[i + 1][0], ring[i + 1][1]],
      ];

      for (const outerSeg of outerSegments) {
        if (segmentsIntersect(outerSeg, innerSeg)) {
          return true;
        }
      }
    }
  }

  return false;
};

export const polygonsContainedWithinPolygon = (
  outer: Geometry,
  layerSource: VectorSource<Geometry> | undefined,
): Feature<Geometry>[] => {
  if (!layerSource) return [];

  return layerSource.getFeatures().filter(f => {
    const inner = f.getGeometry();
    if (!(outer instanceof Polygon) || !(inner instanceof Polygon)) return false;

    // 1. Quick reject: extents do not overlap
    if (!outer.intersectsExtent(inner.getExtent())) {
      return false;
    }

    const innerCoords = inner.getCoordinates();

    // 2. All coordinates of all rings must be inside
    for (const ring of innerCoords) {
      for (const [x, y] of ring) {
        if (!outer.intersectsCoordinate([x, y])) {
          return false;
        }
      }
    }

    // 3. Edge crossing check:
    return !edgeOverlapWithOtherPolygon(outer, inner);
  });
};

export const featureContainsPoint = (geometry: Geometry, point: Point | Coordinate): boolean => {
  if (!geometry) return false;
  const coordinate: Coordinate = point instanceof Point ? point.getCoordinates() : point;
  return geometry.intersectsCoordinate(coordinate);
};

export const isRingInside = (candidateRing: number[][], hostRing: number[][]): boolean => {
  const [x, y] = candidateRing[0];
  return isPointInPolygon([x, y], hostRing);
};

export const isPointInPolygon = (point: number[], polygon: number[][]): boolean => {
  const [px, py] = point;
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];

    // Check if point is exactly on a vertex
    if ((px === xi && py === yi) || (px === xj && py === yj)) {
      return false; // treat boundary as outside
    }

    // Check if point is on a horizontal edge
    const onEdge = yi > py !== yj > py && px === ((xj - xi) * (py - yi)) / (yj - yi) + xi;
    if (onEdge) return false; // treat boundary as outside

    const intersect = yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }

  return inside;
};

export const isPointInsideAnyMultiPolygon = (
  coordinate: number[],
  multipolygons: MultiPolygon[],
): boolean => {
  return multipolygons.some(mp => {
    const coords = mp.getCoordinates(); // [[[outerRing, ...holes], ...]]
    return coords.some(polyCoords => {
      const outerRing = polyCoords[0];
      return isPointInPolygon(coordinate, outerRing);
    });
  });
};

export const mergeMultiPolygons = (
  currentFeature: Feature<Geometry>,
  newFeature: Feature<Geometry>,
) => {
  const currentGeometry = currentFeature.getGeometry();
  const newGeometry = newFeature.getGeometry();

  if (!(currentGeometry instanceof MultiPolygon)) return;
  if (!(newGeometry instanceof Polygon || newGeometry instanceof MultiPolygon)) return;

  const multiCoords = currentGeometry.getCoordinates();

  // Normalize new geometries into an array of polygons
  const newPolygons =
    newGeometry instanceof Polygon ? [newGeometry.getCoordinates()] : newGeometry.getCoordinates();

  newPolygons.forEach(polyCoords => {
    const outerRing = polyCoords[0];
    const innerRings = polyCoords.slice(1);

    // Find all host polygons that contain this polygon
    const hostPolygonIndexes = multiCoords
      .map((existingPolygon, index) => {
        const existingOuterRing = existingPolygon[0];
        return isRingInside(outerRing, existingOuterRing) ? index : -1;
      })
      .filter(index => index !== -1);

    if (hostPolygonIndexes.length > 0) {
      // Add as hole to all matching host polygons
      hostPolygonIndexes.forEach(idx => {
        const targetPolygon = multiCoords[idx];
        targetPolygon.push(outerRing);
        innerRings.forEach(hole => targetPolygon.push(hole));
      });
    } else {
      // Add as new polygon in multipolygon
      multiCoords.push([outerRing, ...innerRings]);
    }
  });
  return multiCoords;
};

export const removePolygonFromMultiPolygon = (
  multiPolygon: MultiPolygon,
  coordinate: number[],
): MultiPolygon | null => {
  const coords = multiPolygon.getCoordinates(); // [[[outer, holes], ...]]

  // Find the polygon index that contains the clicked point
  const polygonIndex = coords.findIndex(polyCoords => {
    const outerRing = polyCoords[0];
    return isPointInPolygon(coordinate, outerRing);
  });

  if (polygonIndex === -1) return null; // No polygon clicked

  // Remove that polygon
  coords.splice(polygonIndex, 1);

  if (coords.length === 0) {
    return null; // MultiPolygon is now empty
  }

  // Return a new MultiPolygon with updated coordinates
  return new MultiPolygon(coords);
};

export const removeInteractions = (
  olmap: OlMap | undefined,
  interactionTypes: InteractionConstructor[],
): void => {
  if (!olmap) return;

  olmap
    .getInteractions()
    .getArray()
    .forEach(interaction => {
      interactionTypes.forEach(ctor => {
        if (interaction instanceof ctor) {
          olmap.removeInteraction(interaction);
        }
      });
    });
};

export const clearAllInteractions = (olmap: OlMap) => {
  if (!olmap) return;

  removeInteractions(olmap, [Draw, Modify, Snap, Translate]);
  const scaleRotLayer = olmap
    .getAllLayers()
    .find(l => l.getClassName() === SCALABLE_ROTATABLE_LAYER);
  if (scaleRotLayer) {
    olmap.removeLayer(scaleRotLayer);
  }
};

export const addDrawInteraction = (olmap: OlMap | undefined, editState: EditState | undefined) => {
  if (!olmap || !editState) return;
  if (
    !olmap
      .getInteractions()
      .getArray()
      .find(i => i instanceof Draw)
  ) {
    olmap.addInteraction(editState.draw);
  }
};

export const addModifyInteraction = (
  olmap: OlMap | undefined,
  editState: EditState | undefined,
) => {
  if (!olmap || !editState) return;
  if (
    !olmap
      .getInteractions()
      .getArray()
      .find(i => i instanceof Modify)
  ) {
    olmap.addInteraction(editState.modify);
  }
};
